import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { CREATORS } from "../data/creators";
import StoryViewer from "./StoryViewer";

export default function StoriesSection() {
  const [viewer, setViewer] = useState(null); // creator index or null

  return (
    <section id="creators" className="mx-auto max-w-7xl px-6 py-28 md:px-12 md:py-40">
      <div className="mb-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-4 text-[10px] uppercase tracking-[0.4em] text-white/40"
        >
          Tap to watch
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-serif-lux text-4xl font-light tracking-tight md:text-6xl"
        >
          Meet Our Creators
        </motion.h2>
      </div>

      <div className="mx-auto grid max-w-2xl grid-cols-2 gap-4 md:gap-6">
        {CREATORS.map((c, i) => (
          <motion.button
            key={c.slug}
            data-testid={`story-card-${c.slug}`}
            onClick={() => setViewer(i)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.12 }}
            whileHover={{ y: -6 }}
            className="group relative aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 text-left"
          >
            <img
              src={c.cover}
              alt={c.name}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/40" />

            {/* story ring avatar */}
            <div className="absolute left-4 top-4 z-10">
              <div
                className="rounded-full p-[2px]"
                style={{ background: `linear-gradient(135deg, ${c.accent}, #ffffff)` }}
              >
                <img src={c.avatar} alt="" className="h-11 w-11 rounded-full border-2 border-black object-cover" />
              </div>
            </div>

            {/* play hint */}
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-md opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <Play size={20} className="ml-0.5 text-white" fill="white" />
              </div>
            </div>

            <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-5">
              <h3 className="font-serif-lux text-2xl font-light leading-tight tracking-tight text-white md:text-3xl">
                {c.name}
              </h3>
              <p className="mt-1 text-[11px] tracking-wide text-white/60">{c.handle}</p>
              <p className="mt-2 text-xs font-light leading-snug text-white/70 line-clamp-2">{c.tagline}</p>
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link
          to={`/creator/${CREATORS[0].slug}`}
          data-testid="creators-explore-link"
          className="text-[11px] uppercase tracking-[0.3em] text-white/50 transition-colors hover:text-white"
        >
          Explore creator profiles →
        </Link>
      </div>

      {viewer !== null && (
        <StoryViewer creators={CREATORS} startIndex={viewer} onClose={() => setViewer(null)} />
      )}
    </section>
  );
}
